import { useClient } from "#/hook/useClient";
import type { ClientsReportRow, ClientsReportRowStatus, ClientsReportSummary } from "#/types/report";
import { formatDate } from "#/utils/dateTimeUtils";
import { formatCurrency } from "#/utils/formatCurrency";
import { Badge, SegmentedControl, Table } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReportLink from "./ReportLink";
import ReportSection from "./ReportSection";

interface ClientsSectionProps {
  data: ClientsReportSummary;
  tooltip?: ReactNode;
}

type SortKey = "value" | "quantity";

const STATUS_BADGE: Record<ClientsReportRowStatus, { color: string; key: string }> = {
  ok: { color: "teal", key: "reports.inventoryScan.clients.status.ok" },
  quiet: { color: "red", key: "reports.inventoryScan.clients.status.quiet" },
  never: { color: "red", key: "reports.inventoryScan.clients.status.never" },
  unknown: { color: "yellow", key: "reports.inventoryScan.clients.status.unknown" },
};

const isAttention = (status: ClientsReportRowStatus): boolean =>
  status === "quiet" || status === "never";

/**
 * "Clients" section: what each sub-client receives, from PRV arrivals.
 * Rendered only when the backend returns it (Predicted Deliveries enabled).
 */
const ClientsSection = ({ data, tooltip }: ClientsSectionProps) => {
  const { t } = useTranslation();
  const { client } = useClient();
  const [sortKey, setSortKey] = useState<SortKey>("value");

  const rows = useMemo(() => {
    const byKey = (row: ClientsReportRow) =>
      sortKey === "value" ? row.valueDelivered : row.itemsDelivered;
    // Never-delivered rows stay at the bottom whatever the sort key.
    return [...data.rows].sort(
      (a, b) =>
        Number(a.status === "never") - Number(b.status === "never") ||
        byKey(b) - byKey(a) ||
        b.itemsDelivered - a.itemsDelivered ||
        a.subClientName.localeCompare(b.subClientName),
    );
  }, [data.rows, sortKey]);

  const tiles = [
    {
      label: t("reports.inventoryScan.clients.tiles.served"),
      value: data.subClientsServed.toLocaleString(),
      detail: t("reports.inventoryScan.clients.tiles.servedDetail", { total: data.rows.length }),
    },
    {
      label: t("reports.inventoryScan.clients.tiles.items"),
      value: data.itemsDelivered.toLocaleString(),
      detail: null,
    },
    {
      label: t("reports.inventoryScan.clients.tiles.value"),
      value: formatCurrency(data.valueDelivered),
      detail: null,
    },
    {
      label: t("reports.inventoryScan.clients.tiles.quiet"),
      value: data.quietCount.toLocaleString(),
      detail:
        data.neverCount > 0
          ? t("reports.inventoryScan.clients.tiles.quietDetail", { count: data.neverCount })
          : null,
    },
  ];

  return (
    <ReportSection
      title={t("reports.inventoryScan.clients.title")}
      tooltip={tooltip}
      subtitle={
        <>
          <span>{t("reports.inventoryScan.clients.subtitle")}</span>
          <span>
            {data.periodStart
              ? t("reports.inventoryScan.clients.period", {
                  start: formatDate(data.periodStart, client),
                  end: formatDate(data.periodEnd, client),
                })
              : t("reports.inventoryScan.clients.noPeriod")}
            {" · "}
            {data.prvLastRunAt
              ? t("reports.inventoryScan.clients.prvRun", {
                  date: formatDate(data.prvLastRunAt, client),
                })
              : t("reports.inventoryScan.clients.prvNoRun")}
          </span>
        </>
      }
      rightSection={
        rows.length > 0 ? (
          <SegmentedControl
            size="xs"
            value={sortKey}
            onChange={(value) => setSortKey(value as SortKey)}
            data={[
              { label: t("reports.inventoryScan.clients.sortValue"), value: "value" },
              { label: t("reports.inventoryScan.clients.sortQuantity"), value: "quantity" },
            ]}
          />
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {tiles.map((tile) => (
            <div
              key={tile.label}
              className="flex flex-col gap-1 rounded-lg border border-sky-200 bg-sky-50 p-4"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                {tile.label}
              </span>
              <span className="text-2xl font-extrabold text-sky-700">{tile.value}</span>
              {tile.detail && <span className="text-xs text-sky-700/80">{tile.detail}</span>}
            </div>
          ))}
        </div>

        {data.categoriesMissingCost > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <IconAlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              {t("reports.inventoryScan.clients.missingCost", { count: data.categoriesMissingCost })}
            </span>
          </div>
        )}

        {rows.length === 0 ? (
          <span className="text-sm text-gray-500">
            {t("reports.inventoryScan.clients.noSubClients")}
          </span>
        ) : (
          <Table
            striped
            highlightOnHover
            withTableBorder
            layout="fixed"
            classNames={{
              th: "whitespace-normal break-words align-top",
              td: "whitespace-normal break-words align-top",
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th className="w-[24%]">
                  {t("reports.inventoryScan.clients.columns.subClient")}
                </Table.Th>
                <Table.Th className="text-right">
                  {t("reports.inventoryScan.clients.columns.items")}
                </Table.Th>
                <Table.Th className="text-right">
                  {t("reports.inventoryScan.clients.columns.value")}
                </Table.Th>
                <Table.Th className="text-right">
                  {t("reports.inventoryScan.clients.columns.share")}
                </Table.Th>
                <Table.Th>{t("reports.inventoryScan.clients.columns.lastDelivery")}</Table.Th>
                <Table.Th className="text-right">
                  {t("reports.inventoryScan.clients.columns.daysSince")}
                </Table.Th>
                <Table.Th>{t("reports.inventoryScan.clients.columns.status")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => {
                const badge = STATUS_BADGE[row.status];
                return (
                  <Table.Tr key={row.subClientId} className={isAttention(row.status) ? "bg-red-50" : ""}>
                    <Table.Td className="font-medium">
                      <ReportLink href={`/sub-clients/${row.subClientId}`}>{row.subClientName}</ReportLink>
                    </Table.Td>
                    <Table.Td className="text-right">{row.itemsDelivered.toLocaleString()}</Table.Td>
                    <Table.Td className="text-right">{formatCurrency(row.valueDelivered)}</Table.Td>
                    <Table.Td className="text-right">{row.sharePercent}%</Table.Td>
                    <Table.Td>{row.lastDeliveryAt ? formatDate(row.lastDeliveryAt, client) : "--"}</Table.Td>
                    <Table.Td className={`text-right ${isAttention(row.status) ? "font-semibold text-red-600" : ""}`}>
                      {row.daysSinceLastDelivery ?? "--"}
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={badge.color} size="sm">
                        {t(badge.key)}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        <span className="text-xs text-gray-500">{t("reports.inventoryScan.clients.statusHelp")}</span>
      </div>
    </ReportSection>
  );
};

export default ClientsSection;
